// filepath: frontend/src/components/layout/Sidebar.tsx
import { NavLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, LayoutDashboard, Truck, FileText, BarChart2,
  HelpCircle, Warehouse, DollarSign,
  ChevronRight, Search, ExternalLink, LayoutGrid, UserPlus, Radar, Brain,
  ShieldCheck, Mail, Clock, MailCheck, X,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { Role } from '@/types';
import { PotopodWordmark } from '@/components/brand/PotopodWordmark';
import { AddUserModal } from '@/components/AddUserModal';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  roles: Role[];
  badge?: number;
  group?: 'ops' | 'execution' | 'intelligence' | 'support';
  /** When set, only render this entry if the org has the matching capability. */
  capability?: 'jira';
}

export function Sidebar() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { recentShipments, sidebarOpen, setSidebarOpen } = useUIStore();
  const [addUserOpen, setAddUserOpen] = useState(false);
  if (!user) return null;

  const close = () => setSidebarOpen(false);

  const canAddUser = user.role === 'ceo' || user.role === 'account_manager';

  const navItems: NavItem[] = [
    // Filing Clerk
    { to: '/filing/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} />, roles: ['filing_clerk'], group: 'ops' },
    { to: '/hub/brokerage', label: 'Brokerage', icon: <Package size={18} />, roles: ['filing_clerk'], group: 'ops' },
    { to: '/hub/control-tower', label: 'Control Tower', icon: <Radar size={18} />, roles: ['filing_clerk'], group: 'ops' },
    { to: '/hub/ttt', label: 'TTT Board', icon: <Brain size={18} />, roles: ['filing_clerk'], group: 'ops', capability: 'jira' },
    { to: '/filing/compliance', label: 'Compliance Desk', icon: <ShieldCheck size={18} />, roles: ['filing_clerk'], group: 'execution' },
    { to: '/filing/asycuda', label: 'ASYCUDA Workspace', icon: <FileText size={18} />, roles: ['filing_clerk'], group: 'execution' },
    { to: '/filing/tariff', label: 'Tariff Lookup', icon: <Search size={18} />, roles: ['filing_clerk'], group: 'execution' },
    { to: '/settings/email', label: 'Email Intake', icon: <Mail size={18} />, roles: ['filing_clerk'], group: 'support' },

    // Document Runner
    { to: '/runner/queue', label: 'My Queue', icon: <Truck size={18} />, roles: ['document_runner'], group: 'ops' },
    { to: '/hub/control-tower', label: 'Control Tower', icon: <Radar size={18} />, roles: ['document_runner'], group: 'ops' },
    { to: '/settings/email', label: 'Email Intake', icon: <Mail size={18} />, roles: ['document_runner'], group: 'support' },

    // Account Manager
    { to: '/account/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} />, roles: ['account_manager'], group: 'ops' },
    { to: '/account/workbench', label: 'Email Workbench', icon: <MailCheck size={18} />, roles: ['account_manager'], group: 'ops' },
    { to: '/hub/brokerage', label: 'Brokerage', icon: <Package size={18} />, roles: ['account_manager'], group: 'ops' },
    { to: '/hub/control-tower', label: 'Control Tower', icon: <Radar size={18} />, roles: ['account_manager'], group: 'ops' },
    { to: '/hub/ttt', label: 'TTT Board', icon: <Brain size={18} />, roles: ['account_manager'], group: 'ops', capability: 'jira' },
    { to: '/account/shipments', label: 'Shipments', icon: <Package size={18} />, roles: ['account_manager'], group: 'execution' },
    { to: '/account/containers', label: 'Containers', icon: <Warehouse size={18} />, roles: ['account_manager'], group: 'execution' },
    { to: '/account/jira', label: 'Jira Tickets', icon: <ExternalLink size={18} />, roles: ['account_manager'], group: 'support', capability: 'jira' },
    { to: '/settings/email', label: 'Email Intake', icon: <Mail size={18} />, roles: ['account_manager'], group: 'support' },

    // Accounts
    { to: '/accounts/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} />, roles: ['accounts'], group: 'ops' },
    { to: '/hub/brokerage', label: 'Brokerage', icon: <Package size={18} />, roles: ['accounts'], group: 'ops' },
    { to: '/accounts/aging', label: 'AR Aging', icon: <Clock size={18} />, roles: ['accounts'], group: 'execution' },
    { to: '/accounts/invoicing', label: 'Invoicing', icon: <DollarSign size={18} />, roles: ['accounts'], group: 'execution' },
    { to: '/accounts/costing', label: 'Costing', icon: <BarChart2 size={18} />, roles: ['accounts'], group: 'intelligence' },
    { to: '/settings/email', label: 'Email Intake', icon: <Mail size={18} />, roles: ['accounts'], group: 'support' },

    // CEO (read-only viewer of the two products; admin pages removed)
    { to: '/hub/brokerage', label: 'Brokerage', icon: <Package size={18} />, roles: ['ceo'], group: 'ops' },
    { to: '/hub/control-tower', label: 'Control Tower', icon: <Radar size={18} />, roles: ['ceo'], group: 'ops' },
    { to: '/account/workbench', label: 'AM Workbench', icon: <MailCheck size={18} />, roles: ['ceo'], group: 'ops' },
    { to: '/hub/ttt', label: 'TTT Board', icon: <Brain size={18} />, roles: ['ceo'], group: 'ops', capability: 'jira' },
    { to: '/settings/email', label: 'Email Intake', icon: <Mail size={18} />, roles: ['ceo'], group: 'support' },
  ];

  // Org capability filter — undefined treated as "enabled" for backward compat
  // with sessions established before /auth/me started returning capabilities.
  const caps = user.capabilities;
  const jiraEnabled = caps?.jira ?? true;
  const visible = navItems
    .filter((item) => item.roles.includes(user.role as Role))
    .filter((item) => {
      if (item.capability === 'jira') return jiraEnabled;
      return true;
    });
  const visibleByGroup = {
    ops: visible.filter((i) => i.group === 'ops'),
    execution: visible.filter((i) => i.group === 'execution'),
    intelligence: visible.filter((i) => i.group === 'intelligence'),
    support: visible.filter((i) => i.group === 'support'),
  };

  const textActive = '#E47527';
  const textDefault = '#838383';
  const recentPrefix = user.role === 'account_manager' || user.role === 'ceo' ? '/account/shipments' : '/shipments';
  const showGroupHeaders = visible.length > 4;

  return (
    <AnimatePresence>
      {sidebarOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={close}
            aria-hidden
          />
          <motion.aside
            className="fixed left-0 top-0 z-50 w-64 flex flex-col h-full overflow-y-auto rounded-r-3xl"
            style={{
              background: 'linear-gradient(185deg, rgba(12,24,42,0.97), rgba(8,15,30,0.98))',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}
            initial={{ x: -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            aria-label="Main navigation"
          >
            <div style={{ height: '2px', background: 'linear-gradient(90deg, #E47527, #00A9BC)', position: 'absolute', top: 0, left: 0, right: 0 }} />
            {/* Logo + close */}
            <div className="px-6 pt-6 pb-4 flex items-center justify-between">
              <PotopodWordmark className="w-[148px]" />
              <button
                onClick={close}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                style={{ color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(255,255,255,0.08)' }}
                aria-label="Close menu"
              >
                <X size={16} />
              </button>
            </div>

      {/* Hub shortcut + Add User */}
      <div className="px-4 pb-3 flex flex-col gap-1.5">
        <button
          onClick={() => { navigate('/hub'); close(); }}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs transition-colors hover:bg-white/5"
          style={{ color: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <LayoutGrid size={14} />
          <span>Module Hub</span>
        </button>
        {canAddUser && (
          <button
            onClick={() => setAddUserOpen(true)}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs transition-colors hover:bg-white/5"
            style={{ color: '#E47527', border: '1px solid rgba(228,117,39,0.25)', background: 'rgba(228,117,39,0.06)' }}
          >
            <UserPlus size={14} />
            <span>Add User</span>
          </button>
        )}
      </div>

      <AddUserModal open={addUserOpen} onClose={() => setAddUserOpen(false)} />

      {/* Nav */}
      <nav className="flex-1 px-4 space-y-3" aria-label="Navigation links">
        <NavSection title={showGroupHeaders ? 'Overview' : ''} items={visibleByGroup.ops} textActive={textActive} textDefault={textDefault} onNavigate={close} />
        <NavSection title={showGroupHeaders ? 'Operations' : ''} items={visibleByGroup.execution} textActive={textActive} textDefault={textDefault} onNavigate={close} />
        <NavSection title={showGroupHeaders ? 'Analytics' : ''} items={visibleByGroup.intelligence} textActive={textActive} textDefault={textDefault} onNavigate={close} />
        <NavSection title={showGroupHeaders ? 'Support' : ''} items={visibleByGroup.support} textActive={textActive} textDefault={textDefault} onNavigate={close} />
      </nav>

      {/* Recent Shipments */}
      {recentShipments.length > 0 && (
        <div className="px-4 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.09)' }}>
          <p className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: textDefault }}>
            Recent
          </p>
          {recentShipments.slice(0, 5).map((s) => (
            <NavLink
              key={s.id}
              to={`${recentPrefix}/${s.id}`}
              onClick={close}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-full text-xs hover:bg-white/5 transition-colors"
              style={{ color: textDefault, fontFamily: 'JetBrains Mono, monospace', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <ChevronRight size={12} />
              {s.reference}
            </NavLink>
          ))}
        </div>
      )}

      {/* Help */}
      <div className="px-4 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.09)' }}>
        <NavLink
          to="/help"
          onClick={close}
          className="flex items-center gap-3 px-3 py-2.5 rounded-full text-sm hover:bg-white/5 transition-colors"
          style={{ color: textDefault, border: '1px solid rgba(255,255,255,0.08)' }}
          aria-label="Help Centre"
        >
          <HelpCircle size={18} />
          Help Centre
        </NavLink>
      </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function NavSection({
  title,
  items,
  textActive,
  textDefault,
  onNavigate,
}: {
  title: string;
  items: NavItem[];
  textActive: string;
  textDefault: string;
  onNavigate?: () => void;
}) {
  if (!items.length) return null;
  return (
    <div>
      {title && (
        <p className="px-2 pb-1 text-[10px] uppercase tracking-[0.22em]" style={{ color: 'rgba(255,255,255,0.45)' }}>
          {title}
        </p>
      )}
      <div className="space-y-1">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-full text-sm transition-all ${
                isActive ? 'font-semibold' : 'hover:bg-white/5'
              }`
            }
            style={({ isActive }) => ({
              color: isActive ? textActive : textDefault,
              border: isActive
                ? '1px solid rgba(228,117,39,0.4)'
                : '1px solid rgba(255,255,255,0.05)',
              background: isActive
                ? 'linear-gradient(135deg, rgba(228,117,39,0.18), rgba(0,169,188,0.12))'
                : 'rgba(255,255,255,0.02)',
              boxShadow: isActive ? '0 10px 22px rgba(8,15,30,0.2), inset 0 1px 0 rgba(255,255,255,0.2)' : undefined,
            })}
            aria-current="page"
          >
            {item.icon}
            <span className="flex-1">{item.label}</span>
            {item.badge ? (
              <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: '#E47527', color: 'white' }}>
                {item.badge}
              </span>
            ) : null}
          </NavLink>
        ))}
      </div>
    </div>
  );
}
